import { useState } from "react";
import AutoCompleteAddress from "../autocomplete/AutoCompleteAddress";
import ValidationErrorMessage from "./ValidationErrorMessage";
import { useQuery } from "@tanstack/react-query";
import { hentAdresse } from "../../api/adressevaelger";
import { AdresseDTO } from "../../interfaces/adresse";
import { AdressevaelgerAdresse } from "../../interfaces/adressevaelger";
import {
  AdresseDTOToString,
  adressevaelgerToAdresseDTO,
} from "../../util/adresse";
import IsRequired from "./common/IsRequired";
import FormControlButtonSmall from "../button/FormControlButtonSmall";

type FormikAddressControlProps<U> = FormikControlProps<U> & {
  config: IAddressControl<U>;
  value?: AdresseDTO;
};

export default function AddressFormControl<U>({
  config,
  formik,
  value,
  disabled,
}: FormikAddressControlProps<U>) {
  // destructs
  const { setFieldValue, touched, errors, setTouched } = formik;

  const { id, label, isRequired } = config;
  const setThisTouched = () => setTouched({ ...touched, [id]: true });

  // state of the art
  const [selectedItem, setSelectedItem] = useState<
    AdressevaelgerAdresse | undefined
  >(undefined);

  // handlers
  function remove() {
    setCaptureFocusOnChange(true);
    setThisTouched();
    setFieldValue(id, undefined);
  }

  // business
  const darAdresseId = value?.darAdresseId ?? selectedItem?.id_lokalid;
  const { data: address, isLoading } = useQuery({
    queryKey: ["adressevaelger-adresse", darAdresseId],
    queryFn: () => hentAdresse(darAdresseId!),
    enabled: !!darAdresseId,
  });

  const fetchedItem: { id: string; tekst: string } | undefined = address
    ? {
        id: address.id_lokalid,
        tekst: address.adressebetegnelse,
      }
    : isLoading
      ? {
          id: value?.darAdresseId ?? "",
          tekst: "Henter ...",
        }
      : undefined;
  const [captureFocusOnChange, setCaptureFocusOnChange] = useState(false);

  return (
    <>
      <label className="form-label" htmlFor={id}>
        {label}
        {isRequired && <IsRequired />}
      </label>
      {value && fetchedItem && (
        <FormControlButtonSmall
          disabled={disabled}
          selectedItem={{ id: fetchedItem.id, header: fetchedItem.tekst }}
          remove={remove}
          focusOnMount={captureFocusOnChange}
        />
      )}
      {value && selectedItem && !fetchedItem && (
        <FormControlButtonSmall
          disabled={disabled}
          selectedItem={{
            id: selectedItem.id_lokalid,
            header: selectedItem.adressebetegnelse,
          }}
          remove={remove}
          focusOnMount={captureFocusOnChange}
        />
      )}
      {value && !selectedItem && !fetchedItem && (
        <FormControlButtonSmall
          disabled={disabled}
          selectedItem={{
            id: value.darAdresseId ?? "-1",
            header: AdresseDTOToString(value),
          }}
          remove={remove}
          focusOnMount={captureFocusOnChange}
        />
      )}
      {!value && (
        <AutoCompleteAddress
          onBlur={() => {
            setThisTouched();
          }}
          selectedItem={selectedItem}
          classNameContainer={`w-100${
            touched[id] && (errors as any)[id] ? " is-invalid" : ""
          }`}
          onSelectedItemChange={(adresse) => {
            setCaptureFocusOnChange(true);
            setFieldValue(id, adressevaelgerToAdresseDTO(adresse));
            setSelectedItem(adresse);
          }}
          focusOnMount={captureFocusOnChange}
        />
      )}
      <ValidationErrorMessage
        touched={touched[id]}
        errorMessage={(errors as any)[id]}
      />
    </>
  );
}
